import { useSyncExternalStore } from "react";

/* ══════════════════════════════════════════════════════════════════
   Ob die globale Suche offen ist — ein Schalter für die ganze Anwendung.

   Dieselbe Bauform wie beim Nachschlagen (`schale/nachschlagen.ts`), aus
   demselben Grund: Aufgemacht wird sie vom Knopf im Kopf und vom
   Tastenkürzel, gezeichnet in der Schale — drei Stellen in drei Zweigen
   des Baums. Ein Kontext um die ganze Anwendung, nur damit ein Knopf mit
   einem Fenster redet, wäre mehr Bau als Sache.

   **Kein `localStorage`.** Eine Suche ist eine Frage, keine Ablage.
   ══════════════════════════════════════════════════════════════════ */

let offen = false;
const hoerer = new Set<() => void>();

function melden(): void {
  hoerer.forEach((ruf) => ruf());
}

/* ══ Signal (07.10.2026): am Schreibtisch kein Fenster mehr ══
   Luca: *„Die Suchleiste oben soll keine Pop-up-Suchleiste aufmachen,
   sondern die bestehende, sodass man einfach nur dadrin eintippen kann."*
   Steht das Feld im Kopf, meldet es sich hier an — dann heißt „Suche
   öffnen" (Kürzel, `/suche`): den Fokus in dieses Feld legen. Ohne Feld
   (Telefon, Tablett) bleibt es das Fenster. */
let feldZiel: (() => void) | null = null;

export function sucheFeldAnmelden(ziel: () => void): () => void {
  feldZiel = ziel;
  return () => { if (feldZiel === ziel) feldZiel = null; };
}

export function sucheOeffnen(): void {
  if (feldZiel) { feldZiel(); return; }
  if (offen) return;
  offen = true;
  melden();
}

export function sucheSchliessen(): void {
  if (!offen) return;
  offen = false;
  melden();
}

function anmelden(ruf: () => void): () => void {
  hoerer.add(ruf);
  return () => { hoerer.delete(ruf); };
}

const jetzt = () => offen;
const nie = () => false;

export function useSucheOffen(): boolean {
  return useSyncExternalStore(anmelden, jetzt, nie);
}

/**
 * „Kunden, Aufträge und Belege" — die Sorten, die dieser Mensch durchsuchen
 * darf, als Aufzählung (B-14, 03.10.2026). Der Leertext der Telefon-Suche
 * versprach „Geräte" auch dem Außendienst, der die Sorte gar nicht sieht.
 */
export function sortenAufzaehlung(namen: readonly string[], und: string): string {
  if (namen.length <= 1) return namen[0] ?? "";
  return `${namen.slice(0, -1).join(", ")} ${und} ${namen[namen.length - 1]}`;
}

/**
 * Wohin der Fokus nach dem Schließen zurückgeht — das Element, das ihn
 * **vor** dem Öffnen hatte (B-12). `body` und nichts sind kein Ziel.
 */
export function fokusVorher(
  aktiv: { focus?: () => void; tagName?: string } | null | undefined,
): { focus?: () => void } | null {
  if (!aktiv || aktiv.tagName === "BODY" || typeof aktiv.focus !== "function") return null;
  return aktiv;
}
